// src/components/JoinMagnificos.tsx
import React from "react";

type Item = {
  id: string;
  title: string;
  subtitle: string;
};

type Props = {
  accentHex?: string;              // color de acento (default #E88809)
  items?: Item[];
  onSchedule?: () => void;         // handler del botón
};

const defaultItems: Item[] = [
  { id: "i1", title: "Información", subtitle: "Dossier completo de la franquicia" },
  { id: "i2", title: "Asesoría", subtitle: "Consulta personalizada gratuita" },
  { id: "i3", title: "Soporte", subtitle: "Acompañamiento paso a paso" },
];

const JoinMagnificos: React.FC<Props> = ({
  accentHex = "#E88809",
  items = defaultItems,
  onSchedule,
}) => {
  return (
    <section
      aria-label="¿Listo para ser parte de Los Magníficos?"
      className="w-full bg-black text-white py-12 sm:py-16"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-[60%_1fr] gap-8 lg:gap-10">
        {/* Izquierda: copy + CTA */}
        <div className="max-w-sm lg:max-w-none text-center lg:text-left">
          <h2
            className="font-extrabold text-2xl sm:text-3xl md:text-4xl lg:text-5xl"
            style={{ fontSize: 'clamp(1.8rem, 5vw, 3rem)', lineHeight: '1.4' }}
          >
            <span className="block sm:inline">¿Estás listo para ser</span>{" "}
            <span className="block sm:inline">parte de <span style={{ color: accentHex }}>Los Magníficos</span>?</span>
          </h2>

          <p
            className="mt-6 text-neutral-200 text-base sm:text-lg md:text-xl"
            style={{ fontSize: 'clamp(1rem, 3vw, 1.5rem)', lineHeight: '1.6' }}
          >
            Hablemos por WhatsApp. Te enviaremos toda la información, el dossier
            de franquicia y resolveremos todas tus dudas.
          </p>

          <div className="mt-8">
            <button
              type="button"
              onClick={onSchedule}
              className="inline-flex items-center justify-center rounded-md px-5 py-2.5 font-semibold text-black hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-white/20"
              style={{ backgroundColor: accentHex, fontSize: 'clamp(0.9rem, 2vw, 1rem)' }}
              aria-label="Agendar una cita por WhatsApp"
            >
              Agenda una cita
            </button>
          </div>

          <p className="mt-4 text-sm text-neutral-400" style={{ lineHeight: '1.5' }}>
            Respuesta inmediata · Dossier completo · Sin compromiso
          </p>
        </div>

        {/* Derecha: cards */}
        <div className="lg:pl-4">
          {/* Carrusel en mobile */}
          <div className="lg:hidden">
            <div
              className="flex gap-4 overflow-x-auto snap-x snap-mandatory pb-2"
              style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
              aria-label="Beneficios y acompañamiento"
            >
              {items.map((it) => (
                <article
                  key={it.id}
                  className="min-w-[90%] snap-center rounded-lg bg-neutral-800/70 hover:scale-105 hover:-translate-y-2 transition-all duration-300 ease-in-out cursor-pointer hover:shadow-xl"
                  style={{
                    border: `0.09375rem solid ${accentHex}`,
                    height: '7.12rem',
                    padding: '1.60556rem 2.09619rem',
                    width: '17rem'
                  }}
                >
                  <div className="flex flex-col items-center justify-center h-full">
                    <h3
                      className="font-semibold text-center"
                      style={{ color: accentHex, fontSize: "1.3rem" }}
                    >
                      {it.title}
                    </h3>
                    <p className="mt-1 text-center text-neutral-200" style={{ fontSize: "1.15rem" }}>
                      {it.subtitle}
                    </p>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Apiladas en desktop */}
          <div className="hidden lg:flex lg:flex-col lg:gap-4">
            {items.map((it) => (
              <article
                key={it.id}
                className="rounded-lg bg-neutral-800/70 hover:scale-105 hover:-translate-y-2 transition-all duration-300 ease-in-out cursor-pointer hover:shadow-xl"
                style={{
                  border: `0.09375rem solid ${accentHex}`,
                  height: '7.12rem',
                  padding: '1.60556rem 2.09619rem',
                  width: '100%'
                }}
              >
                <div className="flex flex-col items-center justify-center h-full">
                  <h3
                    className="font-semibold text-center"
                    style={{ color: accentHex, fontSize: "1.3rem" }}
                  >
                    {it.title}
                  </h3>
                  <p className="mt-1 text-center text-neutral-200" style={{ fontSize: "1.15rem" }}>
                    {it.subtitle}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default JoinMagnificos;